import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { overrideAssessment } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  ChevronLeft, CheckCircle, AlertTriangle, XCircle,
  Pencil, User, BarChart2, Shield, Info, Bot,
  ChevronDown, ChevronUp,
} from 'lucide-react';

const RESULT_META = {
  SUITABLE:   { bg: '#F8FAFC', text: '#166534', border: '#DCFCE7', label: 'Suitable', icon: CheckCircle },
  UNSUITABLE: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA', label: 'Unsuitable', icon: XCircle },
  BORDERLINE: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A', label: 'Borderline', icon: AlertTriangle },
};

const ACTION_META = {
  PROCEED:                { bg: '#F8FAFC', text: '#166534', border: '#DCFCE7' },
  LEGAL_ALERT:            { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
  MANUAL_REVIEW:          { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  ENHANCED_DUE_DILIGENCE: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  ESCALATE_TO_MLRO:       { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
};

const Badge = ({ children, meta, theme }) => (
  <span style={{
    background: meta?.bg || theme.bgInput,
    color: meta?.text || theme.textSecondary,
    border: `1px solid ${meta?.border || theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {children || '-'}
  </span>
);

const SuitabilityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const a = location.state?.assessment;

  const [overriding, setOverriding] = useState(false);
  const [overrideResult, setOverrideResult] = useState('SUITABLE');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideMsg, setOverrideMsg] = useState(null);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [assessment, setAssessment] = useState(a);
  const [showReasoning, setShowReasoning] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const handleOverride = async () => {
    if (overrideReason.trim().length < 50) {
      setOverrideMsg({ type: 'error', text: 'Reason must be at least 50 characters.' });
      return;
    }

    setOverrideLoading(true);
    setOverrideMsg(null);

    try {
      await overrideAssessment(assessment.id, {
        new_result: overrideResult,
        reason: overrideReason,
      });

      setAssessment(prev => ({
        ...prev,
        suitability_result: overrideResult,
        recommended_action:
          overrideResult === 'SUITABLE'
            ? 'PROCEED'
            : overrideResult === 'UNSUITABLE'
              ? 'LEGAL_ALERT'
              : 'MANUAL_REVIEW',
        reasoning: `[COMPLIANCE OFFICER OVERRIDE] Previous AI result: ${prev.suitability_result}. Override reason: ${overrideReason}`,
      }));

      setOverrideMsg({ type: 'success', text: `Assessment overridden to ${overrideResult}.` });
      setOverriding(false);
      setOverrideReason('');
    } catch (err) {
      setOverrideMsg({ type: 'error', text: err.response?.data?.error || 'Override failed.' });
    } finally {
      setOverrideLoading(false);
    }
  };

  if (!assessment) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary }}>
        Assessment not found.{' '}
        <button onClick={() => navigate('/suitability')} style={{
          background: 'none',
          border: 'none',
          color: '#2563EB',
          cursor: 'pointer',
          fontWeight: 600,
        }}>
          Go back
        </button>
      </div>
    );
  }

  const resultMeta = RESULT_META[assessment.suitability_result] || RESULT_META.BORDERLINE;
  const ResultIcon = resultMeta.icon || AlertTriangle;
  const isOverride = assessment.reasoning?.startsWith('[COMPLIANCE OFFICER OVERRIDE]');
  const overrideParts = isOverride
    ? assessment.reasoning?.match(/\[COMPLIANCE OFFICER OVERRIDE\] Previous AI result: (\w+)\. Override reason: (.+)/)
    : null;

  const shapScore = (() => {
    if (!assessment.shap_values) return null;
    const s = assessment.shap_values;
    const pos =
      (s.knowledge_and_experience || 0) +
      (s.financial_situation || 0) +
      (s.investment_objectives || 0) +
      (s.esg_alignment || 0);
    const pen = Math.abs(s.inconsistency_penalty || 0);
    return Math.round(Math.max(0, Math.min(100, (pos - pen) * 100)));
  })();

  const Card = ({ children, style = {} }) => (
    <div style={{
      background: theme.bgCard,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadowSm,
      marginBottom: 16,
      ...style,
    }}>
      {children}
    </div>
  );

  const CardHeader = ({ icon: Icon, title }) => (
    <div style={{
      padding: '14px 16px',
      borderBottom: `1px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      {Icon && <Icon size={15} color={theme.textSecondary} />}
      <span style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>{title}</span>
    </div>
  );

  const CardBody = ({ children }) => (
    <div style={{ padding: '16px' }}>{children}</div>
  );

  const Field = ({ label, value }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      padding: '9px 0',
      borderBottom: `1px solid ${theme.border}`,
    }}>
      <span style={{ fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 12, color: theme.textPrimary, fontWeight: 500, textAlign: 'right' }}>{value || '-'}</span>
    </div>
  );

  const secondaryBtn = {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <button onClick={() => navigate('/suitability')} style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: theme.textSecondary,
        fontSize: 12,
        fontWeight: 600,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginBottom: 16,
      }}>
        <ChevronLeft size={14} /> Suitability Assessments
      </button>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        padding: isMobile ? 16 : 20,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 600, color: theme.textTertiary, textTransform: 'uppercase' }}>
              MiFID II Suitability Result
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <ResultIcon size={20} color={resultMeta.text} />
              <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 26, fontWeight: 700, color: theme.textPrimary }}>
                {resultMeta.label}
              </h1>
              <Badge meta={ACTION_META[assessment.recommended_action]} theme={theme}>
                {assessment.recommended_action?.replace(/_/g, ' ')}
              </Badge>
            </div>

            <p style={{ margin: '8px 0 0', fontSize: 13, color: theme.textSecondary }}>
              {assessment.pseudo_id} · {assessment.fund_name} · {assessment.fund_manager_name}
            </p>
          </div>

          {shapScore !== null && (
            <div style={{
              background: theme.bgInput,
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              padding: '10px 14px',
              minWidth: 92,
            }}>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {shapScore}<span style={{ fontSize: 12, color: theme.textTertiary }}>/100</span>
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.textTertiary }}>Score</p>
            </div>
          )}
        </div>
      </div>

      {(assessment.inconsistency_count_high > 0 || assessment.inconsistency_count_medium > 0) && (
        <div style={{
          background: '#FFFBEB',
          borderRadius: 10,
          padding: '13px 15px',
          marginBottom: 16,
          border: '1px solid #FDE68A',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}>
          <AlertTriangle size={16} color="#92400E" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#92400E' }}>Inconsistencies detected</p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#92400E' }}>
              High: {assessment.inconsistency_count_high} · Medium: {assessment.inconsistency_count_medium} · Low: {assessment.inconsistency_count_low}
            </p>
          </div>
        </div>
      )}

      {assessment.legal_alert && (
        <div style={{
          background: '#FEF2F2',
          borderRadius: 10,
          padding: '13px 15px',
          marginBottom: 16,
          border: '1px solid #FECACA',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}>
          <XCircle size={16} color="#991B1B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#991B1B' }}>Legal alert</p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#991B1B', lineHeight: 1.6 }}>{assessment.legal_alert}</p>
          </div>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 380px',
        gap: 16,
      }}>
        <div>
          <Card>
            <CardHeader icon={User} title="Investor Details" />
            <CardBody>
              <Field label="Pseudo ID" value={assessment.pseudo_id} />
              <Field label="Fund" value={assessment.fund_name} />
              <Field label="Fund Manager" value={assessment.fund_manager_name} />
              <Field label="Risk Profile" value={assessment.risk_profile} />
              <Field label="Horizon" value={assessment.investment_horizon} />
              <Field label="Model" value={assessment.model_version} />
              <Field
                label="Assessed"
                value={new Date(assessment.assessed_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              />
            </CardBody>
          </Card>

          {assessment.shap_values && (
            <Card>
              <CardHeader icon={BarChart2} title="Factor Breakdown" />
              <CardBody>
                {(() => {
                  const s = assessment.shap_values;
                  const pen = Math.abs(s.inconsistency_penalty || 0);

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {[
                        { label: 'Knowledge & Experience', key: 'knowledge_and_experience', max: 0.30 },
                        { label: 'Financial Situation', key: 'financial_situation', max: 0.35 },
                        { label: 'Investment Objectives', key: 'investment_objectives', max: 0.30 },
                        { label: 'ESG Alignment', key: 'esg_alignment', max: 0.15 },
                      ].map(f => {
                        const val = s[f.key] || 0;
                        const pct = Math.round((val / f.max) * 100);

                        return (
                          <div key={f.key}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                              <span style={{ fontSize: 12, color: theme.textSecondary }}>{f.label}</span>
                              <span style={{ fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>
                                {Math.round(val * 100)} / {Math.round(f.max * 100)}
                              </span>
                            </div>
                            <div style={{
                              background: theme.bgInput,
                              border: `1px solid ${theme.border}`,
                              borderRadius: 999,
                              height: 7,
                            }}>
                              <div style={{
                                width: `${Math.min(100, pct)}%`,
                                background: '#2563EB',
                                borderRadius: 999,
                                height: 5,
                                margin: 1,
                              }} />
                            </div>
                          </div>
                        );
                      })}

                      {pen > 0 && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                            <span style={{ fontSize: 12, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <AlertTriangle size={12} /> Inconsistency Penalty
                            </span>
                            <span style={{ fontSize: 11, color: '#991B1B', fontWeight: 700 }}>-{Math.round(pen * 100)}</span>
                          </div>
                          <div style={{
                            background: theme.bgInput,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 999,
                            height: 7,
                          }}>
                            <div style={{
                              width: `${Math.min(100, pen * 200)}%`,
                              background: '#991B1B',
                              borderRadius: 999,
                              height: 5,
                              margin: 1,
                            }} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardBody>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <button onClick={() => setShowReasoning(v => !v)} style={{
              width: '100%',
              padding: '14px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: showReasoning ? `1px solid ${theme.border}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {isOverride ? <Shield size={15} color={theme.textSecondary} /> : <Bot size={15} color={theme.textSecondary} />}
                <span style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>
                  {isOverride ? 'Compliance Override' : 'AI Reasoning'}
                </span>
              </div>
              {showReasoning ? <ChevronUp size={15} color={theme.textTertiary} /> : <ChevronDown size={15} color={theme.textTertiary} />}
            </button>

            {showReasoning && (
              <CardBody>
                {isOverride && overrideParts ? (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 12,
                      marginBottom: 14,
                      padding: 12,
                      background: theme.bgInput,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                    }}>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>Previous AI Result</p>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{overrideParts[1]}</p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>New Result</p>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{assessment.suitability_result}</p>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 5px', fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>Officer justification</p>
                    <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>{overrideParts[2]}</p>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>{assessment.reasoning}</p>
                )}
              </CardBody>
            )}
          </Card>

          {assessment.shap_plain_language && (
            <Card>
              <button onClick={() => setShowExplanation(v => !v)} style={{
                width: '100%',
                padding: '14px 16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: showExplanation ? `1px solid ${theme.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Info size={15} color={theme.textSecondary} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>
                    Confidence Explanation
                  </span>
                </div>
                {showExplanation ? <ChevronUp size={15} color={theme.textTertiary} /> : <ChevronDown size={15} color={theme.textTertiary} />}
              </button>

              {showExplanation && (
                <CardBody>
                  <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>
                    {assessment.shap_plain_language}
                  </p>
                  <p style={{ margin: '12px 0 0', fontSize: 11, color: theme.textTertiary }}>
                    EU AI Act Article 13 transparency explanation.
                  </p>
                </CardBody>
              )}
            </Card>
          )}

          <Card>
            <CardHeader icon={Pencil} title="Compliance Officer Override" />
            <CardBody>
              {!overriding ? (
                <div>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: theme.textSecondary, lineHeight: 1.5 }}>
                    Override the AI decision with a written justification. The change is audit logged.
                  </p>

                  <button onClick={() => setOverriding(true)} style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: 8,
                    background: theme.bgInput,
                    color: theme.textSecondary,
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${theme.border}`,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}>
                    <Pencil size={13} /> Override Decision
                  </button>

                  {overrideMsg && (
                    <p style={{
                      marginTop: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: 'center',
                      color: overrideMsg.type === 'success' ? '#166534' : '#991B1B',
                    }}>
                      {overrideMsg.text}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                      New Result
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['SUITABLE', 'BORDERLINE', 'UNSUITABLE'].map(r => {
                        const selected = overrideResult === r;
                        return (
                          <button key={r} onClick={() => setOverrideResult(r)} style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 11,
                            fontWeight: 600,
                            background: selected ? RESULT_META[r]?.bg : theme.bgInput,
                            color: selected ? RESULT_META[r]?.text : theme.textSecondary,
                            border: `1px solid ${selected ? RESULT_META[r]?.border : theme.border}`,
                          }}>
                            {RESULT_META[r]?.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                      Written Justification <span style={{ color: '#991B1B' }}>*</span>
                    </label>
                    <textarea
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      placeholder="Minimum 50 characters required."
                      rows={4}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                    <p style={{ margin: '5px 0 0', fontSize: 11, color: overrideReason.length >= 50 ? '#166534' : theme.textTertiary }}>
                      {overrideReason.length} / 50 minimum
                    </p>
                  </div>

                  <div style={{
                    background: '#FFFBEB',
                    borderRadius: 8,
                    border: '1px solid #FDE68A',
                    padding: '10px 12px',
                    marginBottom: 12,
                    fontSize: 12,
                    color: '#92400E',
                    display: 'flex',
                    gap: 8,
                    lineHeight: 1.5,
                  }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    This override will be permanently recorded in the immutable audit log.
                  </div>

                  {overrideMsg && (
                    <p style={{
                      marginBottom: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      color: overrideMsg.type === 'success' ? '#166534' : '#991B1B',
                    }}>
                      {overrideMsg.text}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setOverriding(false); setOverrideReason(''); setOverrideMsg(null); }} style={secondaryBtn}>
                      Cancel
                    </button>
                    <button onClick={handleOverride} disabled={overrideLoading || overrideReason.trim().length < 50} style={{
                      flex: 2,
                      padding: '10px',
                      borderRadius: 8,
                      background: overrideReason.trim().length < 50 ? theme.bgInput : '#2563EB',
                      color: overrideReason.trim().length < 50 ? theme.textTertiary : 'white',
                      border: `1px solid ${overrideReason.trim().length < 50 ? theme.border : '#2563EB'}`,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: overrideLoading ? 'not-allowed' : 'pointer',
                    }}>
                      {overrideLoading ? 'Overriding...' : 'Confirm Override'}
                    </button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SuitabilityDetail;
